import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { DashboardComponent } from './dashboard/dashboard.component';
import { DevicesComponent } from './devices/devices.component';
import { DeviceDetailComponent } from './device-detail/device-detail.component';
import { LogsComponent } from './logs/logs.component';
import { LoadingScreenComponent } from './widget/loading-screen/loading-screen.component';

// Angular Material
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatTableModule } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AngularMaterialModule } from './angularMaterial/angulaMaterialModules';
import { HeaderComponent } from './navigation/header/header.component';
import { SidenavListComponent } from './navigation/sidenav-list/sidenav-list.component';
import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';


// Flex
import { FlexLayoutModule } from '@angular/flex-layout';

// Firebase
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireAuthGuardModule } from '@angular/fire/compat/auth-guard';
import { AngularFireMessaging, AngularFireMessagingModule } from '@angular/fire/compat/messaging';

import { environment } from '../environments/environment';
import { LoginComponent } from './auth/login/login.component';
import { RegisterComponent } from './auth/register/register.component';

// FontAwesome
import { FontAwesomeModule, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';

import { VimeModule } from '@vime/angular';


// DateTime picker
import {
  NgxMatDatetimePickerModule,
  NgxMatNativeDateModule,
  NgxMatTimepickerModule
} from '@angular-material-components/datetime-picker';


// http
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';

// Locals
import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeNl from '@angular/common/locales/nl';
import { FooterComponent } from './footer/footer.component';
import { library } from '@fortawesome/fontawesome-svg-core';
import { TimeSlotDialogComponent } from './time-slot-dialog/time-slot-dialog.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AddContactDialogComponent } from './add-contact-dialog/add-contact-dialog.component';
import { GenericDialogComponent } from './generic-dialog/generic-dialog.component';
import { GuideComponent } from './guide/guide.component';
import { AddDeviceComponent } from './add-device/add-device.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { TermsComponent } from './terms/terms.component';
import { PrivacyTermsComponent } from './privacy-terms/privacy-terms.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { ErrorService } from './service/error.service';
import { GtagModule } from 'angular-gtag';

import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { MomentModule } from 'ngx-moment';
import { DevicesListComponent } from './devices/devices-list/devices-list.component';
import { DevicesActivatedComponent } from './devices/devices-activated/devices-activated.component';
import { DevicesMapComponent } from './devices/devices-map/devices-map.component';

// maps
import { AgmCoreModule, GoogleMapsAPIWrapper } from '@agm/core';
import { OwnersComponent } from './owners/owners.component';
import { OwnerDetailComponent } from './owner-detail/owner-detail.component';
import { OwnerDevicesListComponent } from './owner-devices/owner-devices-list/owner-devices-list.component';
import { OwnerDevicesMapComponent } from './owner-devices/owner-devices-map/owner-devices-map.component';
import { OwnerAddComponent } from './owner-add/owner-add.component';
import { LinkOwnerDeviceDialogComponent } from './link-owner-device-dialog/link-owner-device-dialog.component';
import { DeviceLogComponent } from './device-log/device-log.component';
import { DeviceIosComponent } from './device-ios/device-ios.component';
import { OwnerUsersComponent } from './owner-users/owner-users.component';
import { AddUserComponent } from './add-user/add-user.component';
import { OwnersListComponent } from './owners-list/owners-list.component';
import { LinkDeviceOwnerDialogComponent } from './link-device-owner-dialog/link-device-owner-dialog.component';
import { DeviceIosEditorComponent } from './device-ios-editor/device-ios-editor.component';
import { AccessMethodsComponent } from './access-methods/access-methods.component';
import { AccessMethodsEditorComponent } from './access-methods-editor/access-methods-editor.component';
import { DevicePermissionDialogEditorComponent } from './device-permission-dialog-editor/device-permission-dialog-editor.component';
import { AddUserDevicesListComponent } from './add-user/add-user-devices-list/add-user-devices-list.component';
import { AddUserDevicesMapComponent } from './add-user/add-user-devices-map/add-user-devices-map.component';
import { CodeListComponent } from './code-list/code-list.component';
import { CodeListEditorComponent } from './code-list-editor/code-list-editor.component';

import { MatIconModule } from "@angular/material/icon";
import { DeviceDetailEditorComponent } from './device-detail-editor/device-detail-editor.component';
import { AccessLogComponent } from './access-log/access-log.component';
import { AccessMethodsGuideComponent } from './access-methods-guide/access-methods-guide.component';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { MoveBollardDialogComponent } from './move-bollard-dialog/move-bollard-dialog.component';
import { AddGroupComponent } from './add-group/add-group.component';
import { AddGroupDevicesListComponent } from './add-group/add-group-devices-list/add-group-devices-list.component';
import { AddGroupDevicesMapComponent } from './add-group/add-group-devices-map/add-group-devices-map.component';
import { GroupsComponent } from './groups/groups.component';
import { CodeDescriptionDialogComponent } from './code-description-dialog/code-description-dialog.component';
import { AliasesComponent } from './aliases/aliases.component';
import { LinkAliasDialogComponent } from './link-alias-dialog/link-alias-dialog.component';
import { ProgrammedExecutionsComponent } from './programmed-executions/programmed-executions.component';
import { PeriodicExecutionsComponent } from './periodic-executions/periodic-executions.component';
import { AddPeriodicExecutionsComponent } from './add-periodic-executions/add-periodic-executions.component';
import { AddProgrammedExecutionsComponent } from './add-programmed-executions/add-programmed-executions.component';
import { SearchByMethodDialogComponent } from './search-by-method-dialog/search-by-method-dialog.component';
import { OperationLogComponent } from './operation-log/operation-log.component';
import { AutomationsLogComponent } from './automations-log/automations-log.component';
import { LoaderDialogComponent } from './loader-dialog/loader-dialog.component';
import { UserImportDialogComponent } from './user-import-dialog/user-import-dialog.component';
import { SendEmailDialogComponent } from './send-email-dialog/send-email-dialog.component';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { ExecutionsComponent } from './executions/executions.component';
import { CreateOperatorComponent } from './create-operator/create-operator.component';
import { OtaComponent } from './ota/ota.component';
import { DeviceUsersListDialogComponent } from './device-users-list-dialog/device-users-list-dialog.component';
import { AliasDetailComponent } from './alias-detail/alias-detail.component';
import { MigrationDialogComponent } from './migration-dialog/migration-dialog.component';
import { JoinComponent } from './join/join.component';
import { RequestsComponent } from './requests/requests.component';
import { ViewRequestDialogComponent } from './view-request-dialog/view-request-dialog.component';
import { InsertuserdialogComponent } from './insertuserdialog/insertuserdialog.component';
import { AutomationsComponent } from './automations/automations.component';
import { AddAutomationComponent } from './add-automation/add-automation.component';
import { RequestTypesComponent } from './request-types/request-types.component';
import { ViewRequestTypeDialogComponent } from './view-request-type-dialog/view-request-type-dialog.component';

registerLocaleData(localeIt, 'it');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeNl, 'nl');


@NgModule({
  declarations: [
    AppComponent,
    DashboardComponent,
    DevicesComponent,
    DeviceDetailComponent,
    LogsComponent,
    HeaderComponent,
    SidenavListComponent,
    LoginComponent,
    RegisterComponent,
    LoadingScreenComponent,
    FooterComponent,
    TimeSlotDialogComponent,
    AddContactDialogComponent,
    GenericDialogComponent,
    GuideComponent,
    AddDeviceComponent,
    PrivacyComponent,
    TermsComponent,
    PrivacyTermsComponent,
    ResetPasswordComponent,
    DevicesListComponent,
    DevicesMapComponent,
    OwnersComponent,
    OwnerDetailComponent,
    OwnerDevicesListComponent,
    OwnerDevicesMapComponent,
    OwnerAddComponent,
    LinkOwnerDeviceDialogComponent,
    DeviceLogComponent,
    DeviceIosComponent,
    OwnerUsersComponent,
    AddUserComponent,
    OwnersListComponent,
    LinkDeviceOwnerDialogComponent,
    DeviceIosEditorComponent,
    DevicePermissionDialogEditorComponent,
    AddUserDevicesListComponent,
    AddUserDevicesMapComponent,
    AccessMethodsComponent,
    AccessMethodsEditorComponent,
    CodeListComponent,
    CodeListEditorComponent,
    DeviceDetailEditorComponent,
    GroupsComponent,
    AddGroupComponent,
    AddGroupDevicesListComponent,
    AddGroupDevicesMapComponent,
    AccessLogComponent,
    AccessMethodsGuideComponent,
    ConfirmDialogComponent,
    MoveBollardDialogComponent,
    CodeDescriptionDialogComponent,
    AliasesComponent,
    LinkAliasDialogComponent,
    ProgrammedExecutionsComponent,
    PeriodicExecutionsComponent,
    AddPeriodicExecutionsComponent,
    AddProgrammedExecutionsComponent,
    DevicesActivatedComponent,
    SearchByMethodDialogComponent,
    OperationLogComponent,
    AutomationsLogComponent,
    LoaderDialogComponent,
    UserImportDialogComponent,
    SendEmailDialogComponent,
    ExecutionsComponent,
    CreateOperatorComponent,
    OtaComponent,
    DeviceUsersListDialogComponent,
    AliasDetailComponent,
    MigrationDialogComponent,
    JoinComponent,
    RequestsComponent,
    ViewRequestDialogComponent,
    InsertuserdialogComponent,
    AutomationsComponent,
    AddAutomationComponent,
    RequestTypesComponent,
    ViewRequestTypeDialogComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    MatDialogModule,
    MatIconModule,
    AgmCoreModule.forRoot({
      apiKey: environment.onecontrol.apiKey,
      libraries: []
    }),
    BrowserAnimationsModule,
    ReactiveFormsModule,
    AngularMaterialModule,
    MatTableModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatSortModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatRadioModule,
    MatFormFieldModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatMenuModule,
    AngularFireModule.initializeApp(environment.firebase),
    //AngularFireMessagingModule,
    AngularFireAuthModule,
    AngularFireAuthGuardModule,
    HttpClientModule,
    FontAwesomeModule,
    FlexLayoutModule,
    ColorPickerModule,
    GtagModule.forRoot({ trackingId: environment.google.trakingId, trackPageviews: true }),
    CKEditorModule,
    MomentModule,
    FormsModule,
    MatCheckboxModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatNativeDateModule,
    VimeModule,
    AngularEditorModule 
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'it' },
    { provide: ErrorHandler, useClass: ErrorService, },
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: {} }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(library: FaIconLibrary /*, messaging: AngularFireMessaging*/) {
    // Add all icons to the library for convenient access in other components
    
    library.addIconPacks(fas, far);

    //messaging.usePublicVapidKey(environment.firebase.webPushCert);
  }
}
